import { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, RefreshCw, Info } from 'lucide-react';
import { ConsentStatusResult, assignExchangeRole, resetAppConsent } from '../../services/api';

interface ConsentWizardProps {
  isOpen: boolean;
  onClose: () => void;
  consentStatus: ConsentStatusResult;
  clientId: string;
  onRefresh: () => void;
}

export default function ConsentWizard({ isOpen, onClose, consentStatus, clientId, onRefresh }: ConsentWizardProps) {
  const [refreshing, setRefreshing] = useState(false);
  const [assigningRole, setAssigningRole] = useState(false);
  const [resettingConsent, setResettingConsent] = useState(false);
  const [roleMessage, setRoleMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Listen for consent-complete messages from the popup window
  useEffect(() => {
    if (!isOpen) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'consent-complete') {
        // Auto-refresh when consent popup completes
        setRefreshing(true);
        onRefresh();
        setTimeout(() => setRefreshing(false), 2000);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen, onRefresh]);

  if (!isOpen) return null;

  const handleRefresh = async () => {
    setRefreshing(true);
    setRoleMessage(null);
    onRefresh();
    setTimeout(() => setRefreshing(false), 2000);
  };

  const getConsentInstructions = (stepName: string): string => {
    const clientName = consentStatus.clientName;

    const baseInstructions =
      `A Microsoft login window will open.\n\n` +
      `SIGN IN AS: A Global Admin from "${clientName}"\n\n` +
      `IMPORTANT:\n` +
      `• Use the CUSTOMER's admin account (not your Umetech account)\n` +
      `• Check "Consent on behalf of your organization"\n` +
      `• Click "Accept" to grant permissions\n\n` +
      `The window will close automatically after consent is granted.\n` +
      `Status will refresh automatically.\n\n` +
      `Click OK to continue.`;

    return `GRANT ${stepName.toUpperCase()} CONSENT\n\n${baseInstructions}`;
  };

  const openConsentWindow = (url: string, stepName: string) => {
    const confirmed = window.confirm(getConsentInstructions(stepName));

    if (confirmed) {
      window.open(url, '_blank', 'width=600,height=700');
    }
  };

  // Reset and re-consent: tries API first, falls back to Azure Portal
  const handleResetAndReconsent = async () => {
    setResettingConsent(true);
    setRoleMessage(null);

    try {
      // Try to delete the service principal via API
      const resetResult = await resetAppConsent(clientId);

      if (resetResult.success) {
        // API worked - open consent window
        window.open(consentStatus.consentUrls.appConsent, '_blank', 'width=600,height=700');
        setRoleMessage({
          type: 'success',
          text: resetResult.wasDeleted
            ? 'Previous consent removed. Complete the consent in the popup window.'
            : 'Ready for consent. Complete the consent in the popup window.'
        });
      } else {
        // API failed - open Azure Portal directly
        openAzurePortalForConsent();
      }
    } catch {
      // API error - open Azure Portal directly
      openAzurePortalForConsent();
    } finally {
      setResettingConsent(false);
    }
  };

  // Open Azure Portal to the app's permissions page for manual consent
  const openAzurePortalForConsent = () => {
    const spId = consentStatus.appConsent.servicePrincipalId;
    const tenantId = consentStatus.tenantId;
    const appId = '9c12a3a0-fdbf-47d5-a008-c9ee50c9058f';

    // Build Azure Portal URL - goes directly to the app's Permissions page
    let portalUrl: string;
    if (spId) {
      // Direct link to app permissions page
      portalUrl = `https://portal.azure.com/${tenantId}/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Permissions/objectId/${spId}/appId/${appId}`;
    } else {
      // Fallback to enterprise apps list
      portalUrl = `https://portal.azure.com/${tenantId}/#view/Microsoft_AAD_IAM/StartboardApplicationsMenuBlade/~/AppAppsPreview`;
    }

    const confirmed = window.confirm(
      `GRANT CONSENT IN AZURE PORTAL\n\n` +
      `A new window will open to Azure Portal.\n\n` +
      `SIGN IN AS: Global Admin from "${consentStatus.clientName}"\n\n` +
      `THEN:\n` +
      `1. Click "Grant admin consent for ${consentStatus.clientName}"\n` +
      `2. Click "Yes" to confirm\n` +
      `3. Close the window and click Refresh here\n\n` +
      `Click OK to open Azure Portal.`
    );

    if (confirmed) {
      window.open(portalUrl, '_blank');
      setRoleMessage({
        type: 'success',
        text: 'Azure Portal opened. Grant consent there, then click Refresh.'
      });
    }
  };

  const handleAssignExchangeRole = async () => {
    setAssigningRole(true);
    setRoleMessage(null);

    try {
      const result = await assignExchangeRole(clientId);

      if (result.success) {
        setRoleMessage({
          type: 'success',
          text: result.alreadyAssigned
            ? 'Exchange Administrator role is already assigned!'
            : 'Exchange Administrator role assigned successfully!'
        });
        // Refresh status after successful assignment
        setTimeout(() => onRefresh(), 1000);
      } else {
        setRoleMessage({
          type: 'error',
          text: result.message || result.error || 'Failed to assign role'
        });
      }
    } catch (err) {
      setRoleMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to assign Exchange role'
      });
    } finally {
      setAssigningRole(false);
    }
  };

  // App consent is only complete if password reset, TAP policy, AND group member permissions are granted
  const appConsentComplete = consentStatus.appConsent.granted &&
    consentStatus.passwordResetPermission?.granted &&
    consentStatus.tapPolicyPermission?.granted &&
    consentStatus.groupMemberPermission?.granted;
  const completedCount =
    (appConsentComplete ? 1 : 0) +
    (consentStatus.graphCliConsent.granted ? 1 : 0) +
    (consentStatus.exchangeRole.assigned ? 1 : 0) +
    (consentStatus.oversiteConsent?.granted ? 1 : 0);
  const allComplete = completedCount === 4;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75" onClick={onClose}></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-border flex justify-between items-center bg-gradient-to-r from-indigo-600 to-purple-600">
            <div>
              <h3 className="text-lg font-semibold text-white">
                Grant Customer Consents
              </h3>
              <p className="text-sm text-indigo-100">
                {consentStatus.clientName}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-white/80 hover:text-white"
                title="Refresh consent status"
              >
                <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="px-6 py-3 bg-muted border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">
                Progress: {completedCount} of 4 complete
              </span>
              {allComplete && (
                <span className="text-sm font-medium text-green-600 dark:text-green-400 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  All consents granted!
                </span>
              )}
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${allComplete ? 'bg-green-500' : 'bg-indigo-500'}`}
                style={{ width: `${(completedCount / 4) * 100}%` }}
              />
            </div>
          </div>

          {/* Instructions */}
          <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-100 dark:border-blue-900">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">All consent steps require the customer's Global Admin</p>
                <p className="text-xs">
                  For steps 1, 2 & 4: Sign in with an admin account from <strong>{consentStatus.clientName}</strong>.
                  Check "Consent on behalf of your organization" when prompted.
                </p>
              </div>
            </div>
          </div>

          {/* Content - 3 Simple Buttons */}
          <div className="px-6 py-5 space-y-4">
            {/* Step 1: App Consent */}
            <div className={`p-4 rounded-lg border ${
              appConsentComplete
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : consentStatus.appConsent.granted
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                : 'bg-muted border-border'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {appConsentComplete ? (
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  ) : consentStatus.appConsent.granted ? (
                    <AlertCircle className="h-6 w-6 text-yellow-500 mr-3" />
                  ) : (
                    <div className="h-6 w-6 rounded-full border-2 border-border flex items-center justify-center text-sm font-medium text-muted-foreground mr-3">
                      1
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium text-foreground">App Consent</h4>
                    <p className="text-xs text-muted-foreground">
                      {consentStatus.appConsent.granted && !appConsentComplete
                        ? 'Needs update for new permissions'
                        : 'Umetech Cross-Tenant Reports'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={consentStatus.appConsent.granted && !appConsentComplete
                    ? handleResetAndReconsent
                    : () => openConsentWindow(consentStatus.consentUrls.appConsent, 'App')
                  }
                  disabled={appConsentComplete || resettingConsent}
                  className={`px-4 py-2 rounded-md font-medium text-sm ${
                    appConsentComplete
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                      : consentStatus.appConsent.granted
                      ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {appConsentComplete
                    ? 'Done'
                    : resettingConsent
                    ? 'Resetting...'
                    : consentStatus.appConsent.granted
                    ? 'Reset & Re-consent'
                    : 'Grant Consent'}
                </button>
              </div>
            </div>

            {/* Step 2: Graph CLI */}
            <div className={`p-4 rounded-lg border ${
              consentStatus.graphCliConsent.granted
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-muted border-border'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {consentStatus.graphCliConsent.granted ? (
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  ) : (
                    <div className="h-6 w-6 rounded-full border-2 border-border flex items-center justify-center text-sm font-medium text-muted-foreground mr-3">
                      2
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium text-foreground">Graph CLI</h4>
                    <p className="text-xs text-muted-foreground">Microsoft Graph Command Line</p>
                  </div>
                </div>
                <button
                  onClick={() => openConsentWindow(consentStatus.consentUrls.graphCliConsent, 'Graph CLI')}
                  disabled={consentStatus.graphCliConsent.granted}
                  className={`px-4 py-2 rounded-md font-medium text-sm ${
                    consentStatus.graphCliConsent.granted
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {consentStatus.graphCliConsent.granted ? 'Done' : 'Grant Consent'}
                </button>
              </div>
            </div>

            {/* Step 3: Exchange Role */}
            <div className={`p-4 rounded-lg border ${
              consentStatus.exchangeRole.assigned
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-muted border-border'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {consentStatus.exchangeRole.assigned ? (
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  ) : (
                    <div className="h-6 w-6 rounded-full border-2 border-border flex items-center justify-center text-sm font-medium text-muted-foreground mr-3">
                      3
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium text-foreground">Exchange Role</h4>
                    <p className="text-xs text-muted-foreground">Exchange Administrator assignment</p>
                  </div>
                </div>
                <button
                  onClick={handleAssignExchangeRole}
                  disabled={consentStatus.exchangeRole.assigned || assigningRole || !consentStatus.appConsent.granted}
                  className={`px-4 py-2 rounded-md font-medium text-sm ${
                    consentStatus.exchangeRole.assigned
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                      : !consentStatus.appConsent.granted
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : assigningRole
                      ? 'bg-red-400 text-white'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {consentStatus.exchangeRole.assigned ? 'Done' : assigningRole ? 'Assigning...' : 'Assign Role'}
                </button>
              </div>
              {!consentStatus.appConsent.granted && !consentStatus.exchangeRole.assigned && (
                <p className="text-xs text-orange-500 mt-2 ml-9">Complete Step 1 first</p>
              )}
            </div>

            {/* Step 4: OverSite */}
            <div className={`p-4 rounded-lg border ${
              consentStatus.oversiteConsent?.granted
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-muted border-border'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {consentStatus.oversiteConsent?.granted ? (
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  ) : (
                    <div className="h-6 w-6 rounded-full border-2 border-border flex items-center justify-center text-sm font-medium text-muted-foreground mr-3">
                      4
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium text-foreground">OverSite</h4>
                    <p className="text-xs text-muted-foreground">OverSite M365 Monitoring</p>
                  </div>
                </div>
                <button
                  onClick={() => openConsentWindow(consentStatus.consentUrls.oversiteConsent, 'OverSite')}
                  disabled={consentStatus.oversiteConsent?.granted}
                  className={`px-4 py-2 rounded-md font-medium text-sm ${
                    consentStatus.oversiteConsent?.granted
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {consentStatus.oversiteConsent?.granted ? 'Done' : 'Grant Consent'}
                </button>
              </div>
            </div>

            {/* Role assignment message */}
            {roleMessage && (
              <div className={`p-3 rounded-lg flex items-start ${
                roleMessage.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              }`}>
                {roleMessage.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                )}
                <span className="text-sm">{roleMessage.text}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-muted">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <p className="text-xs text-muted-foreground">
                  Tenant: <code className="font-mono text-xs">{consentStatus.tenantId.substring(0, 8)}...</code>
                </p>
                <button
                  onClick={handleResetAndReconsent}
                  disabled={resettingConsent}
                  className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 underline"
                  title="Force delete service principal and re-consent (use if permissions still fail after showing complete)"
                >
                  {resettingConsent ? 'Resetting...' : 'Force Reset'}
                </button>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent"
                >
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
